import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { UserService } from 'src/app/shared/service/user.service';
import { User } from 'src/app/shared/models/user.model';
import Cookies from 'js-cookie';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  constructor(private http: HttpService, private userService: UserService) {}

  signIn(email: string, password: string, code?: string): Observable<any> {
    if (code) {
      return this.http.request('post', '/v1/user/sign-in', {
        email,
        password,
        code
      });
    } else {
      return this.http.request('post', '/v1/user/sign-in', {
        email,
        password,
      });
    }
  }

  signUp(email: string, password: string, recaptchaToken: string): Observable<any> {
    return this.http.request('post', '/v1/user/sign-up', {
      email,
      password,
      recaptcha_token: recaptchaToken
    });
  }

  setToken(token: string, rememberMe: boolean): void {
    if (rememberMe) {
      localStorage.setItem('admin_token', token);
    } else {
      Cookies.set('admin_token', token);
    }
  }

  set userRole(role: string | undefined) {
    if (typeof role === 'string') {
      localStorage.setItem('role', role);
    }
  }

  get userRole(): string | undefined {
    return localStorage.getItem('role') || undefined;
  }

  signOut(): void {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('role');
    Cookies.remove('admin_token');
  }

  isLoggedIn(): Observable<boolean> {
    return this.http.request('get', '/v1/user').pipe(
      map((response: any) => {
        this.setData(response.data);
        return true;
      }),
      catchError((val) => of(false))
    );
  }

  setData(data: any): void {
    this.userService.userStream.next(new User(data));
    localStorage.setItem('user', JSON.stringify(data));
  }

  getUser(): User | null {
    if (localStorage.getItem('user') !== null) {
      return new User(JSON.parse(localStorage.getItem('user') ?? ''));
    }
    return null;
  }

  getToken(): string | undefined {
    return localStorage.getItem('admin_token') || Cookies.get('admin_token') || undefined;
  }

  setImpersonateToken(token: string): void {
    Cookies.set('token', token, { domain: '.nesoex.com' });
  }

  clearImpersonateToken(token: string): void {
    Cookies.remove('token');
  }

  checkScope(checkType: 'AND' | 'OR', ...scopes: string[]): boolean {
    const user = this.getUser();
    if (user) {
      const userScopeHashObject = new Map();
      for (const scope of user.scopes) {
        userScopeHashObject.set(scope, true);
      }
      for (const scope of scopes) {
        if (!userScopeHashObject.has(scope) && checkType === 'AND') {
          return false;
        }
        if (userScopeHashObject.has(scope) && checkType === 'OR') {
          return true;
        }
      }
      if (checkType === 'AND') {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  getSecretKey(): Observable<any> {
    return this.http.request('get', '/v1/user/google-auth').pipe(map((response: any) => response));
  }

  verifyTwoFactorCode(code: string): Observable<any> {
    return this.http.request('put', '/v1/user/google-auth', {
      code
    });
  }

  disableTwoFactor(password: string): Observable<any> {
    return this.http.request('delete', `/v1/user/google-auth?password=${password}`);
  }
}
